import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Settings,
  UserCog,
  Sparkles,
  Rocket,
  Smartphone,
  Wrench,
  Handshake,
  DollarSign,
  Building2,
  Crown,
  Check,
  ArrowRight,
  Zap,
  Star,
  MessageSquare,
  Code2,
  Store,
  CalendarDays,
  Calculator,
  ExternalLink,
  X
} from 'lucide-react';
import TokenCalculator from './TokenCalculator';
import '../styles/PlansSection.css';

const PlansSection = () => {
  const navigate = useNavigate();
  const [billingType, setBillingType] = useState('usd');
  const [showTokenCalculator, setShowTokenCalculator] = useState(false);

  useEffect(() => {
    if (!showTokenCalculator) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowTokenCalculator(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showTokenCalculator]);

  const plans = [
    {
      id: 1,
      name: 'Pro',
      description: 'Modalidad auto gestión. Todas las herramientas de la plataforma y todos los canales con IA incluida.',
      usdPrice: 60,
      pesosPrice: 90000,
      popular: false,
      features: [
        'Todas las herramientas de la plataforma',
        'Todos los canales (WhatsApp, Instagram, Facebook, TikTok, Telegram, SMS, Email, Webchat, Llamadas)',
        'Inteligencia Artificial avanzada (OpenAI)',
        'ChatBot con procesamiento de lenguaje natural',
        'Bandeja de entrada omnicanal unificada',
        'CRM integrado con seguimiento de contactos',
        'Coexistencia WhatsApp Business',
        'Constructor visual de flujos de trabajo',
        'Automatizaciones avanzadas',
        'Mensajería masiva multicanal',
        'Usuarios y contactos ilimitados',
        'API completa y Webhooks personalizados',
        'Más de 50 integraciones'
      ],
      buttonText: 'Suscribirse',
      showPrice: true,
      paymentUrls: {
        pesos: 'https://mpago.la/2qeSpHr',
        usd: 'https://buy.stripe.com/4gM4gz8B0f9Qc6t5VV0Fi0q'
      }
    },
    {
      id: 2,
      name: 'Full',
      description: 'Incluye asesoría completa de estrategia comercial, mejora de procesos y optimización. Nuestro equipo configura toda la automatización de tu empresa.',
      usdPrice: 450,
      pesosPrice: 550000,
      popular: true,
      features: [
        'Todo lo incluido en el Plan Pro',
        'Asesoría completa de estrategia comercial',
        'Análisis y mejora de procesos comerciales',
        'Optimización continua de consultas y respuestas',
        'Configuración 100% personalizada por nuestro equipo',
        'Grupo de WhatsApp dedicado con nuestro equipo',
        'Reunión inicial de onboarding y análisis de tu marca',
        'Propuesta de soluciones tecnológicas a medida',
        'Demo antes de conectar a canales reales',
        'Conexión a canales reales (WhatsApp, Instagram, TikTok, etc.)',
        'Refinamiento continuo y actualizaciones a pedido',
        'Posibilidad de upgrades e integraciones con sistemas externos'
      ],
      buttonText: 'Suscribirse',
      showPrice: true,
      paymentUrls: {
        pesos: 'https://mpago.la/1Naaky3',
        usd: 'https://buy.stripe.com/28E28r9F43r87Qd3NN0Fi0p'
      }
    }
  ];

  const processSteps = [
    {
      number: 1,
      title: 'Grupo de WhatsApp y reunión inicial',
      description: 'Creamos un grupo dedicado y nos reunimos para conocer tu marca en profundidad.'
    },
    {
      number: 2,
      title: 'Análisis de estrategia actual',
      description: 'Estudiamos tu estrategia comercial actual para identificar oportunidades de mejora.'
    },
    {
      number: 3,
      title: 'Propuesta de soluciones',
      description: 'Proponemos soluciones tecnológicas adaptadas a las necesidades de tu negocio.'
    },
    {
      number: 4,
      title: 'Toma de información del negocio',
      description: 'Obtenemos toda la información relevante de tu negocio, productos y servicios.'
    },
    {
      number: 5,
      title: 'Configuración inicial',
      description: 'Nuestro equipo de expertos trabaja en tu cuenta configurando todas las automatizaciones.'
    },
    {
      number: 6,
      title: 'Entrega de Demo',
      description: 'El cliente prueba el sistema completo antes de conectarlo a los canales reales.'
    },
    {
      number: 7,
      title: 'Conexión a canales reales',
      description: 'Conectamos a tus canales reales: WhatsApp, Instagram, Telegram, TikTok y más.'
    },
    {
      number: 8,
      title: 'Refinamiento',
      description: 'Nuestro equipo queda a disposición para refinar, actualizar y agregar nuevas funcionalidades.'
    }
  ];

  const partnerModels = [
    { icon: DollarSign, label: 'Referral Partner', value: '10% por venta' },
    { icon: Wrench, label: 'Implementador', value: '10% + 100% implementación' },
    { icon: Building2, label: 'Negocio / Agencia', value: 'Markup libre + descuentos' },
    { icon: Crown, label: 'Etiqueta Blanca', value: 'Tu marca y dominio propio' }
  ];

  const tiendaRunaTools = [
    { icon: Store, label: 'Tienda Online' },
    { icon: Calculator, label: 'Presupuestadores' },
    { icon: CalendarDays, label: 'Agendas y reservas' },
    { icon: Sparkles, label: 'Y más herramientas' }
  ];

  const getCurrentPrice = (plan) => {
    if (!plan.showPrice) return null;
    return billingType === 'usd' ? plan.usdPrice : plan.pesosPrice;
  };

  const getCurrency = () => {
    return billingType === 'usd' ? 'USD' : 'ARS';
  };

  const getPaymentUrl = (plan) => {
    if (plan.paymentUrls) {
      return billingType === 'usd' ? plan.paymentUrls.usd : plan.paymentUrls.pesos;
    }
    return plan.whatsappUrl;
  };

  const handlePlanClick = (plan) => {
    if (plan.id === 2 && plan.name === 'Full') {
      navigate('/planFull');
    } else {
      const url = getPaymentUrl(plan);
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <section id="plans" className="plans-section section section-page">
      <div className="container">
        <motion.div
          className="section-header text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h2>
            Soluciones que impulsan{' '}
            <span className="text-gradient">tu Negocio</span>
          </h2>
          <p>
            Automatización de atención al cliente y desarrollos de software a medida.
            Elegí el camino que mejor se adapte a lo que tu empresa necesita hoy.
          </p>
        </motion.div>

        <motion.div
          className="plans-category-nav"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <a href="#automatizacion" className="plans-category-nav__item">
            <MessageSquare size={16} strokeWidth={2.2} />
            <span>Automatización y atención</span>
          </a>
          <a href="#desarrollos" className="plans-category-nav__item">
            <Code2 size={16} strokeWidth={2.2} />
            <span>Desarrollos a medida</span>
          </a>
        </motion.div>

        {/* ═══════════════════════════════════════════
            GRUPO 1 — Automatización y gestión
        ═══════════════════════════════════════════ */}
        <motion.div
          id="automatizacion"
          className="plans-zone plans-zone--automation"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
        >
          <div className="plans-zone__intro">
            <span className="plans-zone__index">01</span>
            <div className="plans-zone__intro-copy">
              <p className="plans-zone__label">
                <MessageSquare size={14} strokeWidth={2.5} />
                Plataforma Runamatic
              </p>
              <h3 className="plans-zone__title">
                Automatización y gestión de la atención al cliente
              </h3>
              <p className="plans-zone__desc">
                Planes para automatizar ventas, soporte y comunicación omnicanal
                con IA. Auto gestión, o con nuestro equipo configurando todo a medida.
              </p>
            </div>
          </div>

          <div className="plans-zone__body">
            <motion.p
              className="plans-unlimited-note"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Todos nuestros planes incluyen{' '}
              <strong>Usuarios y Contactos Ilimitados</strong>
              <span className="plans-unlimited-note__sep">—</span>
              Sin límites, sin restricciones.
            </motion.p>

            <motion.div
              className="billing-toggle"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="toggle-container">
                <span className={`toggle-label ${billingType === 'usd' ? 'active' : ''}`}>
                  USD
                </span>
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    id="billing-toggle"
                    checked={billingType === 'pesos'}
                    onChange={(e) => setBillingType(e.target.checked ? 'pesos' : 'usd')}
                  />
                  <label htmlFor="billing-toggle" className="switch"></label>
                </div>
                <span className={`toggle-label ${billingType === 'pesos' ? 'active' : ''}`}>
                  Pesos ARG
                </span>
              </div>
            </motion.div>

            <div className="ps-showcase">
              <motion.article
                className="ps-card ps-card--pro"
                initial={{ opacity: 0, x: -40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.2, ease: 'easeOut' }}
              >
                <div className="ps-card__inner">
                  <div className="ps-type-chip ps-type-chip--pro">
                    <UserCog size={11} strokeWidth={2.5} />
                    Auto gestión
                  </div>

                  <div className="ps-head">
                    <h3 className="ps-name">Pro</h3>
                    <p className="ps-subtitle">La plataforma completa, en tus manos</p>
                  </div>

                  <div className="ps-price-block">
                    <div className="ps-price-line">
                      <span className="ps-price-sym">$</span>
                      <span className="ps-price-num">
                        {getCurrentPrice(plans[0]).toLocaleString()}
                      </span>
                      <div className="ps-price-meta">
                        <span className="ps-price-currency">{getCurrency()}</span>
                        <span className="ps-price-freq">por mes</span>
                        {billingType === 'pesos' && (
                          <span className="ps-price-iva">sin IVA</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="ps-tokens-tag">
                    <Sparkles size={12} strokeWidth={2.5} />
                    <span>3M tokens OpenAI / mes</span>
                  </div>

                  <p className="ps-feat-title">
                    <Zap size={13} strokeWidth={2.5} />
                    Incluye todo esto:
                  </p>
                  <ul className="ps-feat-list">
                    {plans[0].features.map((f, i) => (
                      <li key={i} className="ps-feat-item">
                        <span className="ps-feat-check ps-feat-check--pro">
                          <Check size={9} strokeWidth={3.5} />
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>

                  <motion.button
                    className="ps-cta ps-cta--pro"
                    onClick={() => handlePlanClick(plans[0])}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    Suscribirse ahora
                    <ArrowRight size={16} strokeWidth={2.5} />
                    <span className="ps-cta__method">
                      {billingType === 'pesos' ? 'MercadoPago' : 'Stripe'}
                    </span>
                  </motion.button>
                </div>
              </motion.article>

              <motion.article
                className="ps-card ps-card--full"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.3, ease: 'easeOut' }}
              >
                <div className="ps-card__border-glow" aria-hidden="true" />

                <div className="ps-card__inner ps-card__inner--full">
                  <div className="ps-rec-badge">
                    <span className="ps-rec-badge__dot" />
                    Recomendado
                  </div>

                  <div className="ps-type-chip ps-type-chip--full">
                    <Settings size={11} strokeWidth={2.5} />
                    Con nuestro equipo
                  </div>

                  <div className="ps-head">
                    <h3 className="ps-name ps-name--full">Full</h3>
                    <p className="ps-subtitle ps-subtitle--full">
                      Estrategia + asesoría + equipo dedicado
                    </p>
                  </div>

                  <div className="ps-price-block">
                    <div className="ps-price-line">
                      <span className="ps-price-sym ps-price-sym--full">$</span>
                      <span className="ps-price-num ps-price-num--full">
                        {getCurrentPrice(plans[1]).toLocaleString()}
                      </span>
                      <div className="ps-price-meta">
                        <span className="ps-price-currency ps-price-currency--full">
                          {getCurrency()}
                        </span>
                        <span className="ps-price-freq ps-price-freq--full">por mes</span>
                        {billingType === 'pesos' && (
                          <span className="ps-price-iva ps-price-iva--full">sin IVA</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="ps-tokens-tag ps-tokens-tag--full">
                    <Sparkles size={12} strokeWidth={2.5} />
                    <span>3M tokens OpenAI / mes</span>
                  </div>

                  <p className="ps-feat-title ps-feat-title--full">
                    <Star size={13} strokeWidth={2.5} />
                    Todo lo del Plan Pro, más:
                  </p>
                  <ul className="ps-feat-list ps-feat-list--full">
                    {plans[1].features.map((f, i) => (
                      <li key={i} className="ps-feat-item ps-feat-item--full">
                        <span className="ps-feat-check ps-feat-check--full">
                          <Check size={9} strokeWidth={3.5} />
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>

                  <motion.button
                    className="ps-cta ps-cta--full"
                    onClick={() => handlePlanClick(plans[1])}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    Ver detalles y contratar
                    <ArrowRight size={16} strokeWidth={2.5} />
                  </motion.button>
                </div>
              </motion.article>
            </div>

            <motion.div
              className="token-calc-trigger-row"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.35 }}
            >
              <p className="token-calc-trigger-row__hint">
                ¿Quieres estimar el consumo de IA?
              </p>
              <button
                type="button"
                className="token-calc-trigger"
                onClick={() => setShowTokenCalculator(true)}
              >
                <Calculator size={15} strokeWidth={2.4} />
                Abrir calculadora de tokens
              </button>
            </motion.div>

            {/* Partners — banda editorial dentro del grupo */}
            <motion.a
              href="/partners"
              className="partners-band"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.45 }}
              whileHover={{ x: 4 }}
            >
              <div className="partners-band__main">
                <div className="partners-band__eyebrow">
                  <Handshake size={15} />
                  Programa de Partners
                </div>
                <h4 className="partners-band__title">
                  Creá una nueva línea de negocio con Runamatic
                </h4>
                <p className="partners-band__desc">
                  Agencias, freelancers y consultores pueden generar ingresos
                  recurrentes ofreciendo automatización de ventas con IA.
                </p>
                <span className="partners-band__cta">
                  Ver Programa de Partners
                  <ArrowRight size={15} strokeWidth={2.5} />
                </span>
              </div>
              <ul className="partners-band__models">
                {partnerModels.map((model) => {
                  const Icon = model.icon;
                  return (
                    <li key={model.label} className="partners-band__model">
                      <Icon size={16} strokeWidth={2.2} />
                      <div>
                        <span className="partners-band__model-label">{model.label}</span>
                        <span className="partners-band__model-value">{model.value}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </motion.a>
          </div>
        </motion.div>

        {/* ═══════════════════════════════════════════
            GRUPO 2 — Desarrollos a medida
        ═══════════════════════════════════════════ */}
        <motion.div
          id="desarrollos"
          className="plans-zone plans-zone--software"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25 }}
        >
          <div className="plans-zone__intro plans-zone__intro--software">
            <span className="plans-zone__index plans-zone__index--software">02</span>
            <div className="plans-zone__intro-copy">
              <p className="plans-zone__label plans-zone__label--software">
                <Code2 size={14} strokeWidth={2.5} />
                Software empresarial
              </p>
              <h3 className="plans-zone__title plans-zone__title--software">
                Desarrollos de software a medida
              </h3>
              <p className="plans-zone__desc plans-zone__desc--software">
                Aplicaciones internas, sistemas de gestión y herramientas digitales
                pensadas para la operación real de tu empresa.
              </p>
            </div>
          </div>

          <div className="software-offerings">
            {/* Desarrollos a medida — fila editorial */}
            <motion.div
              className="software-offering"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="software-offering__meta">
                <span className="software-offering__tag">A medida</span>
                <h4 className="software-offering__name">Desarrollos personalizados</h4>
                <p className="software-offering__lead">
                  Creamos soluciones digitales que se adaptan a tu operación:
                  sistemas de gestión, aplicaciones internas y plataformas a medida.
                </p>
              </div>

              <ul className="software-offering__points">
                <li>
                  <Sparkles size={16} strokeWidth={2.2} />
                  Páginas web modernas
                </li>
                <li>
                  <Rocket size={16} strokeWidth={2.2} />
                  Aplicaciones web escalables
                </li>
                <li>
                  <Smartphone size={16} strokeWidth={2.2} />
                  Soluciones móviles
                </li>
                <li>
                  <Wrench size={16} strokeWidth={2.2} />
                  Integración con sistemas
                </li>
              </ul>

              <motion.a
                href="/desarrollos"
                className="software-offering__action"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Ver servicios de desarrollo
                <ArrowRight size={16} strokeWidth={2.5} />
              </motion.a>
            </motion.div>

            <div className="software-offerings__divider" aria-hidden="true" />

            {/* Tienda Runa — fila editorial */}
            <motion.div
              className="software-offering software-offering--runa"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="software-offering__meta">
                <span className="software-offering__tag software-offering__tag--runa">
                  Tienda Runa
                </span>
                <h4 className="software-offering__name">Herramientas listas para tu empresa</h4>
                <p className="software-offering__lead">
                  Plataforma de Runamatic con herramientas de uso general para
                  empresas, a un menor valor que un desarrollo a medida.
                </p>
                <p className="software-offering__motto">
                  Si no tenemos la herramienta para tu empresa, la creamos.
                </p>
              </div>

              <ul className="software-offering__points software-offering__points--runa">
                {tiendaRunaTools.map((tool) => {
                  const Icon = tool.icon;
                  return (
                    <li key={tool.label}>
                      <Icon size={16} strokeWidth={2.2} />
                      {tool.label}
                    </li>
                  );
                })}
              </ul>

              <motion.a
                href="https://tiendaruna.io"
                target="_blank"
                rel="noopener noreferrer"
                className="software-offering__action software-offering__action--runa"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Ir a Tienda Runa
                <ExternalLink size={15} strokeWidth={2.5} />
              </motion.a>
            </motion.div>
          </div>
        </motion.div>

        {typeof document !== 'undefined' &&
          createPortal(
            <AnimatePresence>
              {showTokenCalculator && (
                <motion.div
                  className="token-calc-drawer-root"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <button
                    type="button"
                    className="token-calc-drawer__backdrop"
                    aria-label="Cerrar calculadora"
                    onClick={() => setShowTokenCalculator(false)}
                  />
                  <motion.aside
                    className="token-calc-drawer"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="token-calc-drawer-title"
                    initial={{ x: '-100%' }}
                    animate={{ x: 0 }}
                    exit={{ x: '-100%' }}
                    transition={{ type: 'tween', duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <div className="token-calc-drawer__header">
                      <div className="token-calc-drawer__heading">
                        <p className="token-calc-drawer__eyebrow">Herramienta</p>
                        <h3 id="token-calc-drawer-title" className="token-calc-drawer__title">
                          Calculadora de tokens
                        </h3>
                      </div>
                      <button
                        type="button"
                        className="token-calc-drawer__close"
                        aria-label="Cerrar calculadora"
                        onClick={() => setShowTokenCalculator(false)}
                      >
                        <X size={20} strokeWidth={2.5} />
                        <span className="token-calc-drawer__close-label">Cerrar</span>
                      </button>
                    </div>
                    <div className="token-calc-drawer__body">
                      <TokenCalculator />
                    </div>
                    <div className="token-calc-drawer__footer">
                      <button
                        type="button"
                        className="token-calc-drawer__footer-close"
                        onClick={() => setShowTokenCalculator(false)}
                      >
                        Cerrar calculadora
                      </button>
                    </div>
                  </motion.aside>
                </motion.div>
              )}
            </AnimatePresence>,
            document.body
          )}

        {/* Proceso Plan Full */}
        <motion.div
          className="runamatic-process-section"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7 }}
        >
          <div className="process-header text-center">
            <h2>
              Proceso <span className="text-gradient">Plan Full</span>
            </h2>
            <p>
              Al suscribirte al Plan Full, seguimos esta serie de pasos para
              configurar tu cuenta con asesoría y seguimiento personalizado:
            </p>
          </div>

          <div className="process-timeline">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.number}
                className="process-step"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
              >
                <div className="step-number">
                  <span>{step.number}</span>
                </div>
                <div className="step-content">
                  <h4>{step.title}</h4>
                  <p>{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PlansSection; 
